/**
 * What the interact control does right now — pure rules, no renderer, no physics.
 *
 * The engine tells us what the player is looking at (already filtered by reach — an
 * out-of-reach object arrives here as `null`); this module decides the action. One rule
 * shapes everything: while carrying, interact means drop — *unless* the thing in hand is
 * a part of the thing under the crosshair, in which case it means attach (design D3/D5).
 * The fit itself is never decided here: `canAttach` in `src/game/assembly.ts` is the one
 * place that knows a leg fits a dog body, so a second assembly is a data row there, not a
 * second branch here.
 */

import { type Attachments, canAttach } from './assembly';
import { OBJECT_KINDS } from './objectKinds';
import type { ObjectKind } from './profile/types';

export type InteractiveKind = 'door' | 'carryable' | 'light-switch' | 'table';

/**
 * What the camera ray currently rests on, within reach. A light switch is identified by
 * the id of the room it lights: one switch per room, so the room id is the switch id.
 * `objectKind` is present only for a `carryable` — it is what lets `availableAction` ask
 * whether the carried object fits this one, without the store or this module knowing the
 * level's contents (design D5).
 */
export type FocusTarget = { id: string; kind: InteractiveKind; objectKind?: ObjectKind };

export type DoorState = 'open' | 'closed';
export type LightState = 'on' | 'off';

export type InteractionVerb =
  | 'open'
  | 'close'
  | 'pick-up'
  | 'drop'
  | 'switch-on'
  | 'switch-off'
  | 'drag'
  | 'attach';

export type InteractionAction =
  | { verb: Exclude<InteractionVerb, 'attach'>; targetId: string }
  | { verb: 'attach'; targetId: string; partKind: ObjectKind };

export type InteractionContext = {
  carriedObjectId: string | null;
  /** The kind of the carried object, alongside its id — null with empty hands. */
  carriedObjectKind: ObjectKind | null;
  doorStates: Readonly<Record<string, DoorState>>;
  roomLights: Readonly<Record<string, LightState>>;
  /** Every attached part in the run, so `canAttach` can tell a full host from a free one. */
  attachments: Attachments;
};

export function availableAction(
  target: FocusTarget | null,
  context: InteractionContext,
): InteractionAction | null {
  if (context.carriedObjectId !== null) {
    if (
      target &&
      target.objectKind &&
      context.carriedObjectKind &&
      canAttach(context.carriedObjectKind, target.objectKind, context.attachments, target.id)
    ) {
      return { verb: 'attach', targetId: target.id, partKind: context.carriedObjectKind };
    }
    return { verb: 'drop', targetId: context.carriedObjectId };
  }
  if (!target) return null;

  if (target.kind === 'door') {
    // A door with no recorded state has never been touched: doors start closed unless
    // the profile opened them, and that initial state is seeded into the store at boot.
    const state = context.doorStates[target.id] ?? 'closed';
    return { verb: state === 'closed' ? 'open' : 'close', targetId: target.id };
  }

  if (target.kind === 'light-switch') {
    // Rooms start lit, so an unrecorded room is on — the opposite default to a door,
    // and the one that means a player never walks into a house that is dark by accident.
    const state = context.roomLights[target.id] ?? 'on';
    return { verb: state === 'on' ? 'switch-off' : 'switch-on', targetId: target.id };
  }

  if (target.kind === 'table') {
    // A table is too heavy to lift, so a press does nothing to it: it is the *hold* that
    // drags, and the hold is read from `InputState.interactHeld` by `DragController`.
    // This verb exists to say so on the prompt — the systems that act on a press
    // deliberately ignore it.
    return { verb: 'drag', targetId: target.id };
  }

  return { verb: 'pick-up', targetId: target.id };
}

const ACTION_LABELS: Readonly<Record<Exclude<InteractionVerb, 'attach'>, string>> = {
  open: 'Open',
  close: 'Close',
  'pick-up': 'Pick up',
  drop: 'Drop',
  'switch-on': 'Turn on',
  'switch-off': 'Turn off',
  drag: 'Drag',
};

/**
 * The text the prompt and the touch button show for an action. Takes the whole action,
 * not just the verb (design D6): attach reads "Attach leg", which needs the part's kind,
 * and the part's kind is on the action, not on a bare verb.
 */
export function actionLabel(action: InteractionAction): string {
  if (action.verb === 'attach') {
    return `Attach ${OBJECT_KINDS[action.partKind].label.toLowerCase()}`;
  }
  return ACTION_LABELS[action.verb];
}
